/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SearchOutlined } from '@ant-design/icons'
import { Col, Row, Space, Input, Image } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { selectCurrentUser } from 'Store/user/selector'
import { formatDateTime, numberWithComma, toDateQuery } from 'helpers/format'
import { TableListPage } from 'Components/common/Table'
import { PrimaryButton } from 'Components/button'
import { Pagination } from 'Components/Pagination'
import { RangeDateItem } from 'Components/form'
import theme from 'Components/common/Theme'
import { getAllBurnPointHistoryAPI } from 'Store/member-point/api'
import { AllBurnPointData, AllBurnPointHistoryReq } from 'Store/member-point/types'
import { selectAllMemberBurnPointHistory } from 'Store/member-point/selector'
import { appConfig } from 'config'
import { ImageFallback } from 'helpers/constanst'

type Props = {} & ReduxProps

const BurnPoint: React.FunctionComponent<Props> = props => {
  const { currentUser, pointRedeem } = props
  const listEvent = pointRedeem.event
  const dispatch = useDispatch()

  const [filter, setFilter] = useState({
    keyword: '',
    start_date: '',
    end_date: '',
    status: '',
    order_by: 'created_at',
    sort: 'desc',
  })
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 10,
  })
  const meta = pointRedeem.meta
  const datas = pointRedeem.data

  const onGetDataList = (): void => {
    const req: AllBurnPointHistoryReq = {
      page: pagination.Page,
      per_page: pagination.RowPerPage,
      order_by: filter.order_by,
      sort: filter.sort,
      start_date: toDateQuery(filter.start_date),
      end_date: toDateQuery(filter.end_date),
      keyword: filter.keyword,
    }
    dispatch(getAllBurnPointHistoryAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }, [filter?.order_by, filter?.sort])

  const onSort = (order_by: string, sort: string) => {
    setFilter({
      ...filter,
      sort,
      order_by,
    })
  }

  useEffect(() => {
    if (currentUser?.accessToken) {
      onGetDataList()
    }
  }, [pagination])

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  useEffect(() => {
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 10
    setPagination({ ...pagination })
  }, [])

  const onFilter = (key: string, val: string) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const handleChangeDate = (statDate: string, endDate: string) => {
    setFilter({
      ...filter,
      start_date: statDate,
      end_date: endDate,
    })
  }

  const columns = [
    {
      title: '',
      dataIndex: 'image_url',
      key: 'image_url',
      width: '80px',
      render: (image_url: string) => (
        <Space>
          <ThumbImage width={50} src={appConfig.assetCdnEndpoint + image_url} fallback={ImageFallback} />
        </Space>
      ),
    },
    {
      title: 'วันที่ทำรายการ',
      key: 'created_at',
      dataIndex: 'created_at',
      render: (date: string) => formatDateTime(date),
      sorter: true,
    },
    {
      title: 'ชื่อลูกค้า/เบอร์โทรศัพท์',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: true,
      render: (val: string, record: AllBurnPointData) => `${record?.full_name || ''}/${record?.tel || ''}`,
    },
    {
      title: 'รายการของรางวัล',
      dataIndex: 'title',
      key: 'title',
      width: '250px',
      render: (val: string) => val || '-',
      sorter: true,
    },
    {
      title: 'จำนวนคะแนนที่ใช้แลก',
      dataIndex: 'burn_value',
      key: 'burn_value',
      sorter: true,
      render: (val: number) => (
        <div>
          <PointBurn>-{numberWithComma(val)}</PointBurn> คะแนน
        </div>
      ),
    },
  ]

  const FilterContent = (
    <FilterWrapper>
      <Row justify="space-between" align="bottom">
        <MyCol xs={24} md={20}>
          <Row gutter={[16, 16]} align="bottom">
            <Col md={10} xs={24}>
              <SearchInput
                placeholder={'ค้นหาเบอร์โทรศัพท์ หรือชื่อลูกค้า'}
                prefix={<SearchOutlined />}
                allowClear
                value={filter.keyword}
                onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
              />
            </Col>
            <Col md={10} xs={24}>
              <RangeDateItem
                placeHolder={['วันที่ทำรายการเริ่มต้น', 'สิ้นสุด']}
                onChange={handleChangeDate}
                startDate={filter.start_date}
                endDate={filter.end_date}
              />
            </Col>
            <Col>
              <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
            </Col>
          </Row>
        </MyCol>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
    />
  )

  return (
    <>
      {FilterContent}
      <TableListPage
        columns={columns}
        datas={datas}
        isLoading={listEvent.IsLoading}
        paginationContent={PaginationContent}
        size="small"
        onSort={onSort}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    pointRedeem: selectAllMemberBurnPointHistory(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const FilterWrapper = styled.div`
  padding: 10px 0 5px 0;
`

const MyCol = styled(Col)`
  margin-top: 10px;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`

const PointBurn = styled.span`
  color: ${theme.colors.error};
  font-size: ${theme.typography.body3};
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 100%;
  object-fit: contain;
`

export default connect(mapStateToProps)(BurnPoint)
